import React, { useState } from "react";
import SubPageLayout from "../../components/shared/SubPageLayout";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Radar, Pie, Bar } from "react-chartjs-2";
import Modal from "../../components/shared/Modal";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
);

const FinancialLiteracy = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedCourse, setSelectedCourse] = useState(null);

  const breadcrumbItems = [
    { label: "Education & Licensing", link: "/education-licensing" },
    {
      label: "Financial Literacy",
      link: "/education-licensing/financial-literacy",
    },
  ];

  // Mock data for advisor performance metrics
  const advisorMetrics = {
    score: 85,
    tier: "Gold",
    completedCourses: 42,
    totalHours: 156,
    ranking: 3,
    totalAdvisors: 125,
  };

  // Mock data for skill proficiency
  const skillProficiency = {
    "Investment Planning": 90,
    "Tax Strategy": 85,
    "Risk Management": 75,
    "Estate Planning": 80,
    "Retirement Planning": 95,
    Insurance: 70,
    "Client Communication": 88,
    "Market Analysis": 82,
  };

  // Mock data for learning progress
  const learningProgress = {
    completed: 65,
    inProgress: 20,
    notStarted: 15,
    weeklyProgress: [12, 19, 15, 25],
    weeklyGoal: 30,
  };

  // Mock data for recommended courses
  const recommendedCourses = [
    {
      id: 1,
      title: "Advanced Tax Strategies",
      provider: "FitMoney",
      type: "Advanced Course",
      duration: "4 hours",
      relevance: "High",
      progress: 0,
      tags: ["Tax Planning", "Advanced"],
      description: "Master complex tax strategies for high-net-worth clients.",
      chapters: [
        "Tax Loss Harvesting",
        "Estate Tax Planning",
        "Business Tax Strategies",
        "International Tax Considerations",
      ],
    },
    {
      id: 2,
      title: "ESG Investment Fundamentals",
      provider: "Investopedia",
      type: "Certification",
      duration: "6 hours",
      relevance: "Trending",
      progress: 25,
      tags: ["ESG", "Sustainable Investing"],
      description:
        "Comprehensive guide to ESG investing principles and implementation.",
    },
    {
      id: 3,
      title: "Client Psychology in Volatile Markets",
      provider: "PersonalFinanceLab",
      type: "Mini Course",
      duration: "2 hours",
      relevance: "Recommended",
      progress: 0,
      tags: ["Client Relations", "Psychology"],
      description:
        "Learn to guide clients through market volatility and uncertainty.",
    },
  ];

  // Mock data for current challenges
  const currentChallenges = [
    {
      id: 1,
      title: "March Mastery Challenge",
      progress: 75,
      endDate: "2025-03-31",
      reward: "500 Points + Gold Badge",
      requirements: [
        { task: "Complete 3 Advanced Courses", done: 2, total: 3 },
        { task: "Score 90%+ on Assessments", done: 1, total: 1 },
        { task: "Contribute 2 Case Studies", done: 1, total: 2 },
      ],
    },
    {
      id: 2,
      title: "ESG Specialist Path",
      progress: 40,
      endDate: "2025-04-15",
      reward: "ESG Specialist Certificate",
      requirements: [
        { task: "Complete ESG Fundamentals", done: 1, total: 1 },
        { task: "Pass ESG Assessment", done: 0, total: 1 },
        { task: "Create ESG Portfolio", done: 0, total: 1 },
      ],
    },
  ];

  // Mock data for achievements
  const achievements = [
    {
      id: 1,
      name: "Investment Master",
      icon: "🏆",
      level: "Gold",
      progress: 100,
      description: "Completed all advanced investment courses",
    },
    {
      id: 2,
      name: "Tax Strategist",
      icon: "📊",
      level: "Silver",
      progress: 75,
      description: "Mastered tax planning fundamentals",
    },
    {
      id: 3,
      name: "Client Champion",
      icon: "🌟",
      level: "Gold",
      progress: 100,
      description: "Outstanding client satisfaction scores",
    },
    {
      id: 4,
      name: "Risk Analyst",
      icon: "📈",
      level: "Bronze",
      progress: 45,
      description: "Developing risk management expertise",
    },
  ];

  const renderHeader = () => (
    <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6 mb-6">
      <div className="grid grid-cols-12 gap-6">
        {/* Advisor Profile */}
        <div className="col-span-3 border-r">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-[#202EC6] flex items-center justify-center">
                <span className="text-2xl text-white font-bold">JD</span>
              </div>
              <div className="absolute -bottom-1 -right-1 bg-[#029831] rounded-full p-1">
                <span className="text-white text-xs font-bold">
                  {advisorMetrics.tier}
                </span>
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">James Doe</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Senior Financial Advisor</p>
              <p className="text-xs text-[#029831] font-medium">
                Rank #{advisorMetrics.ranking} of {advisorMetrics.totalAdvisors}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="col-span-9 grid grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-[#202EC6]">
              {advisorMetrics.score}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Advisor Score</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[#202EC6]">
              {advisorMetrics.completedCourses}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Courses Completed</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[#202EC6]">
              {advisorMetrics.totalHours}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Learning Hours</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[#029831]">
              {achievements.filter((a) => a.level === "Gold").length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Gold Achievements</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabs = () => {
    const tabs = [
      { id: "overview", label: "Overview", icon: "📊" },
      { id: "courses", label: "Learning Path", icon: "📚" },
      { id: "achievements", label: "Achievements", icon: "🏆" },
      { id: "challenges", label: "Challenges", icon: "🎯" },
      { id: "analytics", label: "Analytics", icon: "📈" },
    ];

    return (
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-4 mb-6">
        <div className="flex space-x-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id
                  ? "bg-[#202EC6] text-white"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderOverview = () => (
    <div className="grid grid-cols-12 gap-6">
      {/* Skill Proficiency */}
      <div className="col-span-8">
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            Skill Proficiency
          </h3>
          <div className="h-[300px]">
            <Radar
              data={{
                labels: Object.keys(skillProficiency),
                datasets: [
                  {
                    label: "Proficiency Level",
                    data: Object.values(skillProficiency),
                    backgroundColor: "rgba(32, 46, 198, 0.2)",
                    borderColor: "rgba(32, 46, 198, 1)",
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                scales: {
                  r: {
                    beginAtZero: true,
                    max: 100,
                    ticks: { stepSize: 20 },
                  },
                },
                maintainAspectRatio: false,
              }}
            />
          </div>
        </div>
      </div>

      {/* Learning Progress */}
      <div className="col-span-4">
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            Learning Progress
          </h3>
          <div className="mb-6">
            <Pie
              data={{
                labels: ["Completed", "In Progress", "Not Started"],
                datasets: [
                  {
                    data: [
                      learningProgress.completed,
                      learningProgress.inProgress,
                      learningProgress.notStarted,
                    ],
                    backgroundColor: [
                      "rgba(2, 152, 49, 0.8)",
                      "rgba(32, 46, 198, 0.8)",
                      "rgba(209, 213, 219, 0.8)",
                    ],
                  },
                ],
              }}
              options={{ maintainAspectRatio: false }}
            />
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400">Weekly Goal Progress</div>
            <div className="text-2xl font-bold text-[#029831]">
              {learningProgress.weeklyProgress[3]} /{" "}
              {learningProgress.weeklyGoal} hours
            </div>
          </div>
        </div>
      </div>

      {/* Recommended Courses */}
      <div className="col-span-12">
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Recommended for You
            </h3>
            <button className="text-[#202EC6] hover:underline">
              View All Courses
            </button>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {recommendedCourses.map((course) => (
              <div
                key={course.id}
                className="border rounded-lg p-4 hover:border-[#202EC6] transition-all cursor-pointer"
                onClick={() => setSelectedCourse(course)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-gray-100">{course.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{course.provider}</p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      course.relevance === "High"
                        ? "bg-red-100 text-red-800"
                        : course.relevance === "Trending"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {course.relevance}
                  </span>
                </div>
                <div className="flex items-center space-x-2 mb-3">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{course.type}</span>
                  <span className="text-gray-300">•</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {course.duration}
                  </span>
                </div>
                {course.progress > 0 ? (
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Progress</span>
                      <span className="text-[#029831]">{course.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#029831] rounded-full h-2"
                        style={{ width: `${course.progress}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <button className="w-full bg-[#202EC6] text-white rounded-lg px-4 py-2 hover:bg-opacity-90">
                    Start Course
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Current Challenges */}
      <div className="col-span-12">
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Active Challenges
            </h3>
            <button className="text-[#202EC6] hover:underline">
              View All Challenges
            </button>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {currentChallenges.map((challenge) => (
              <div key={challenge.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-gray-100">
                      {challenge.title}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Ends {new Date(challenge.endDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-[#029831]">
                      {challenge.reward}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {challenge.progress}% Complete
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  {challenge.requirements.map((req, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          req.done === req.total
                            ? "bg-[#029831] text-white"
                            : "border-2 border-gray-300"
                        }`}
                      >
                        {req.done === req.total && (
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {req.task} ({req.done}/{req.total})
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-[#029831] rounded-full h-2"
                      style={{ width: `${challenge.progress}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverview();
      // Add other tab content renders here
      default:
        return renderOverview();
    }
  };

  return (
    <SubPageLayout
      title="Financial Literacy"
      subtitle="Enhance your expertise through continuous learning"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="space-y-6">
        {renderHeader()}
        {renderTabs()}
        {renderContent()}
      </div>

      {/* Course Details Modal */}
      {selectedCourse && (
        <StartCourseModal isOpen={!!selectedCourse} onClose={() => setSelectedCourse(null)} course={selectedCourse} />
      )}
    </SubPageLayout>
  );
};

const StartCourseModal = ({ isOpen, onClose, course }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="2xl">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Start Course: {course?.title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Course Overview
            </h4>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {course?.description}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Course Details
            </h4>
            <dl className="mt-2 space-y-2">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-600 dark:text-gray-400">Duration:</dt>
                <dd className="text-sm text-gray-900 dark:text-gray-100">{course?.duration}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-600 dark:text-gray-400">Difficulty:</dt>
                <dd className="text-sm text-gray-900 dark:text-gray-100">{course?.difficulty}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-600 dark:text-gray-400">Credits:</dt>
                <dd className="text-sm text-gray-900 dark:text-gray-100">{course?.credits} CE Credits</dd>
              </div>
            </dl>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Prerequisites
            </h4>
            <ul className="mt-2 space-y-1">
              {course?.prerequisites?.map((prereq, index) => (
                <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                  • {prereq}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
          Begin Course
        </button>
      </div>
    </Modal>
  );
};

export default FinancialLiteracy;
